/*
 * @Author: Zhouqi
 * @Date: 2025-12-15 11:55:58
 * @LastEditors: Zhouqi
 * @LastEditTime: 2025-12-18 16:19:55
 */
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseSplitDragOptions {
  containerWidth: number;
  isEnabled: boolean;
}

export const useSplitDrag = ({ containerWidth, isEnabled }: UseSplitDragOptions) => {
  const [splitPos, setSplitPos] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 初始化分割位置
  useEffect(() => {
    setSplitPos(containerWidth / 2);
  }, [containerWidth]);

  const handleGlobalMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current || !isEnabled) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const offsetX = e.clientX - containerRect.left;
      const clampedOffset = Math.max(0, Math.min(offsetX, containerRect.width));
      setSplitPos(clampedOffset);
    },
    [isDragging, isEnabled],
  );

  // 处理全局鼠标释放
  const handleGlobalMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 绑定全局事件监听器
  useEffect(() => {
    if (isDragging && isEnabled) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, isEnabled, handleGlobalMouseMove, handleGlobalMouseUp]);

  // 开始拖拽
  const startDrag = useCallback(() => {
    if (isEnabled) {
      setIsDragging(true);
    }
  }, [isEnabled]);

  const resetSplitPos = useCallback(() => {
    setSplitPos(containerWidth / 2);
  }, [containerWidth]);

  return {
    splitPos,
    containerRef,
    startDrag,
    isDragging,
    resetSplitPos,
  };
};
